import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { PrintIcon, CloseIcon } from "../../Icons";

/**
 * Chrome for any print-in-a-new-tab page: a screen-only toolbar plus the
 * printable content. Opening the tab only shows the sheet — the print dialog
 * waits for the toolbar button, so the reader can check the page over first.
 *
 * The toolbar carries `no-print`, and the global `@media print` rules in
 * index.css strip it along with shadows and disabled-input greys so the page
 * lands on paper looking like the form rather than like a web app.
 */

/** Signature images and webfonts must be in before the dialog opens. */
const waitForImages = (root) => {
  if (!root) return Promise.resolve();
  const pending = Array.from(root.querySelectorAll("img"))
    .filter((img) => !img.complete)
    .map(
      (img) =>
        new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        }),
    );
  return Promise.all(pending);
};

/** Gives the browser a chance to lay out before measuring page breaks. */
const nextPaint = () =>
  new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve)),
  );

const PrintShell = ({ title, subtitle, ready = true, onPrinted, contentClassName = "", children }) => {
  const contentRef = useRef(null);
  const [printing, setPrinting] = useState(false);

  const print = useCallback(async () => {
    setPrinting(true);
    try {
      await Promise.all([
        document.fonts?.ready?.catch?.(() => {}) ?? Promise.resolve(),
        waitForImages(contentRef.current),
      ]);
      await nextPaint();
      window.print();
      onPrinted?.();
    } finally {
      setPrinting(false);
    }
  }, [onPrinted]);

  useEffect(() => {
    document.title = title ? `${title} — Print` : "Print";
  }, [title]);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="no-print sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex w-full max-w-4xl items-center gap-3 px-4 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-slate-800">
              {title || "Print"}
            </p>
            {subtitle && (
              <p className="truncate text-[11px] text-slate-500">{subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={print}
            disabled={!ready || printing}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <PrintIcon size={13} />
            {printing ? "Preparing…" : "Print / Save as PDF"}
          </button>
          <button
            type="button"
            onClick={() => window.close()}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100"
            aria-label="Close tab"
            title="Close tab"
          >
            <CloseIcon size={14} />
          </button>
        </div>
      </div>

      <div
        ref={contentRef}
        className={`print-content mx-auto w-full py-4 ${contentClassName}`.trim()}
      >
        {children}
      </div>
    </div>
  );
};

PrintShell.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  /** Keeps the Print button disabled until the content has its data. */
  ready: PropTypes.bool,
  /** Extra classes on the printable content wrapper (e.g. print-only padding tweaks). */
  contentClassName: PropTypes.string,
  /** Fired after each print dialog, for audit logging. */
  onPrinted: PropTypes.func,
  children: PropTypes.node,
};

export default PrintShell;
